import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowLeft, Plus, TrendingUp, TrendingDown, DollarSign, Download, Trash2, X, ChevronLeft, ChevronRight, PieChart, Upload, FileImage } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LivroCaixaPage = () => {
  const { token } = useAuth();
  const [data, setData] = useState({ lancamentos: [], resumo: { total_entradas: 0, total_saidas: 0, saldo: 0, por_categoria: {} } });
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentDate, setCurrentDate] = useState(() => {
    const d = new Date();
    return { mes: d.getMonth() + 1, ano: d.getFullYear() };
  });

  const [formData, setFormData] = useState({
    tipo: 'entrada',
    categoria: 'mensalidade',
    descricao: '',
    valor: '',
    data: new Date().toISOString().split('T')[0],
    forma_pagamento: 'pix',
    observacoes: '',
    comprovante_url: '',
    fotos_evento: []
  });
  const [uploadingComp, setUploadingComp] = useState(false);
  const [uploadingFoto, setUploadingFoto] = useState(false);

  const meses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

  const categoriasEntrada = [
    { value: 'mensalidade', label: '📅 Mensalidade', color: 'text-green-400' },
    { value: 'inscricao', label: '📝 Taxa de Inscrição', color: 'text-green-400' },
    { value: 'loja', label: '🛍️ Venda da Loja', color: 'text-green-400' },
    { value: 'doacao', label: '💝 Doação', color: 'text-green-400' },
    { value: 'verba_prefeitura', label: '🏛️ Verba Pública', color: 'text-green-400' },
    { value: 'evento', label: '🎉 Evento/Ação', color: 'text-green-400' },
    { value: 'outros_entrada', label: '💰 Outras Entradas', color: 'text-green-400' },
  ];

  const categoriasSaida = [
    { value: 'acao_social', label: '❤️ Ação Social', color: 'text-red-400' },
    { value: 'manutencao', label: '🔧 Manutenção', color: 'text-red-400' },
    { value: 'compras', label: '🛒 Compras/Estoque', color: 'text-red-400' },
    { value: 'combustivel', label: '⛽ Combustível', color: 'text-red-400' },
    { value: 'aluguel', label: '🏠 Aluguel/Sede', color: 'text-red-400' },
    { value: 'ajuda_custo', label: '💵 Ajuda de Custo', color: 'text-red-400' },
    { value: 'outros_saida', label: '📤 Outras Saídas', color: 'text-red-400' },
  ];

  const currentCategorias = formData.tipo === 'entrada' ? categoriasEntrada : categoriasSaida;

  useEffect(() => {
    fetchData();
  }, [currentDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/livro-caixa?mes=${currentDate.mes}&ano=${currentDate.ano}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(response.data);
    } catch (error) {
      toast.error('Erro ao carregar livro caixa');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!formData.descricao || !formData.valor || !formData.data) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    if (formData.tipo === 'saida' && !formData.comprovante_url) {
      toast.error('Toda despesa exige comprovante/nota fiscal');
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/livro-caixa`,
        { ...formData, valor: parseFloat(formData.valor) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Lançamento registrado! 💰');
      setShowAdd(false);
      setFormData({
        tipo: 'entrada', categoria: 'mensalidade', descricao: '', valor: '',
        data: new Date().toISOString().split('T')[0], forma_pagamento: 'pix', observacoes: '',
        comprovante_url: '', fotos_evento: []
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar lançamento');
    } finally {
      setSaving(false);
    }
  };

  const uploadArquivo = async (file, tipo = 'comprovante') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('tipo', tipo);
    const r = await axios.post(`${API_URL}/api/admin/comprovantes/upload`, fd, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
    });
    return r.data.url;
  };

  const onUploadComprovante = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingComp(true);
    try {
      const url = await uploadArquivo(file, 'comprovante');
      setFormData({ ...formData, comprovante_url: url });
      toast.success('Comprovante anexado!');
    } catch {
      toast.error('Erro ao enviar comprovante');
    } finally {
      setUploadingComp(false);
    }
  };

  const onUploadFotoEvento = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if ((formData.fotos_evento || []).length >= 2) {
      toast.error('Máximo 2 fotos do evento');
      return;
    }
    setUploadingFoto(true);
    try {
      const url = await uploadArquivo(file, 'foto_evento');
      setFormData({ ...formData, fotos_evento: [...(formData.fotos_evento || []), url] });
      toast.success('Foto adicionada!');
    } catch {
      toast.error('Erro ao enviar foto');
    } finally {
      setUploadingFoto(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover este lançamento?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/livro-caixa/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Lançamento removido');
      fetchData();
    } catch (error) {
      toast.error('Erro ao remover');
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/livro-caixa/export-pdf?mes=${currentDate.mes}&ano=${currentDate.ano}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `livro_caixa_${currentDate.ano}_${String(currentDate.mes).padStart(2, '0')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('PDF baixado!');
    } catch (error) {
      toast.error('Erro ao exportar PDF');
    }
  };

  const handleExportImpactoAnual = async () => {
    try {
      toast.info('Gerando relatório anual...');
      const response = await axios.get(
        `${API_URL}/api/admin/relatorios/impacto-social/${currentDate.ano}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `impacto_social_${currentDate.ano}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Relatório de Impacto Social gerado!');
    } catch (error) {
      toast.error('Erro ao gerar relatório anual');
    }
  };

  const changeMonth = (delta) => {
    let { mes, ano } = currentDate;
    mes += delta;
    if (mes > 12) { mes = 1; ano++; }
    if (mes < 1) { mes = 12; ano--; }
    setCurrentDate({ mes, ano });
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="bg-gradient-to-br from-[#FFD700]/10 to-[#120E1A] p-6 border-b border-[#FFD700]/30">
        <div className="max-w-7xl mx-auto">
          <Link to="/admin" data-testid="caixa-back-button" className="inline-flex items-center gap-2 text-[#A19BAA] hover:text-white mb-4">
            <ArrowLeft size={20} />
            <span>Painel Admin</span>
          </Link>
          <div className="flex flex-wrap justify-between items-center gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <BookOpen className="w-8 h-8 text-[#FFD700]" />
                <h1 className="text-3xl font-black text-white" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="caixa-title">
                  Livro Caixa Digital
                </h1>
              </div>
              <p className="text-[#A19BAA]">Controle financeiro da associação</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleExportImpactoAnual}
                data-testid="export-impacto-anual-button"
                className="bg-gradient-to-r from-[#E2B84A] to-[#FFD700] hover:opacity-90 text-white font-bold rounded-lg px-4 py-2 transition-colors flex items-center gap-2"
                title="Relatório de Impacto Social anual consolidado"
              >
                <Download size={18} />
                Impacto {currentDate.ano}
              </button>
              <button
                onClick={handleExportPDF}
                data-testid="export-pdf-button"
                className="bg-[#E2B84A] hover:bg-[#F0D98C] text-white font-bold rounded-lg px-4 py-2 transition-colors flex items-center gap-2"
              >
                <Download size={18} />
                PDF Mês
              </button>
              <button
                onClick={() => setShowAdd(true)}
                data-testid="add-lancamento-button"
                className="bg-[#FFD700] hover:bg-[#F4C430] text-black font-bold rounded-lg px-4 py-2 transition-colors flex items-center gap-2"
              >
                <Plus size={18} />
                Lançar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Month Navigator */}
      <div className="bg-[#120E1A] border-b border-[#2D243F] p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-4">
          <button 
            onClick={() => changeMonth(-1)} 
            data-testid="prev-month-button"
            className="text-[#A19BAA] hover:text-white p-2"
          >
            <ChevronLeft size={24} />
          </button>
          <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="current-period">
            {meses[currentDate.mes - 1]} {currentDate.ano}
          </h2>
          <button 
            onClick={() => changeMonth(1)} 
            data-testid="next-month-button"
            className="text-[#A19BAA] hover:text-white p-2"
          >
            <ChevronRight size={24} />
          </button>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto">
        {/* Resumo Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-br from-green-900/40 to-green-700/20 border border-green-500/30 rounded-xl p-6" data-testid="resumo-entradas">
            <div className="flex items-center gap-3 mb-3">
              <TrendingUp className="w-8 h-8 text-green-400" />
              <p className="text-green-300 font-bold">Entradas</p>
            </div>
            <p className="text-3xl font-black text-green-400" style={{ fontFamily: 'Outfit, sans-serif' }}>
              R$ {data.resumo.total_entradas.toFixed(2)}
            </p>
          </div>

          <div className="bg-gradient-to-br from-red-900/40 to-red-700/20 border border-red-500/30 rounded-xl p-6" data-testid="resumo-saidas">
            <div className="flex items-center gap-3 mb-3">
              <TrendingDown className="w-8 h-8 text-red-400" />
              <p className="text-red-300 font-bold">Saídas</p>
            </div>
            <p className="text-3xl font-black text-red-400" style={{ fontFamily: 'Outfit, sans-serif' }}>
              R$ {data.resumo.total_saidas.toFixed(2)}
            </p>
          </div>

          <div className={`bg-gradient-to-br ${data.resumo.saldo >= 0 ? 'from-[#FFD700]/40 to-[#F4C430]/20 border-[#FFD700]/30' : 'from-red-900/40 to-red-700/20 border-red-500/30'} border rounded-xl p-6`} data-testid="resumo-saldo">
            <div className="flex items-center gap-3 mb-3">
              <DollarSign className={`w-8 h-8 ${data.resumo.saldo >= 0 ? 'text-[#FFD700]' : 'text-red-400'}`} />
              <p className={`font-bold ${data.resumo.saldo >= 0 ? 'text-[#FFD700]' : 'text-red-300'}`}>Saldo</p>
            </div>
            <p className={`text-3xl font-black ${data.resumo.saldo >= 0 ? 'text-[#FFD700]' : 'text-red-400'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
              R$ {data.resumo.saldo.toFixed(2)}
            </p>
          </div>
        </div>

        {/* Breakdown por Categoria */}
        {Object.keys(data.resumo.por_categoria).length > 0 && (
          <div className="bg-[#120E1A] border border-[#2D243F] rounded-xl p-6 mb-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <PieChart className="w-6 h-6 text-[#E2B84A]" />
              Resumo por Categoria
            </h3>
            <div className="grid md:grid-cols-2 gap-3">
              {Object.entries(data.resumo.por_categoria).map(([cat, vals]) => (
                <div key={cat} className="bg-[#050505] border border-[#2D243F] rounded-lg p-3 flex items-center justify-between">
                  <span className="text-white capitalize">{cat.replace(/_/g, ' ')}</span>
                  <div className="flex gap-3 text-sm">
                    {vals.entradas > 0 && <span className="text-green-400 font-bold">+R$ {vals.entradas.toFixed(2)}</span>}
                    {vals.saidas > 0 && <span className="text-red-400 font-bold">-R$ {vals.saidas.toFixed(2)}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Lançamentos */}
        <div className="bg-[#120E1A] border border-[#2D243F] rounded-xl p-6">
          <h3 className="text-xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Lançamentos do Período ({data.resumo.total_lancamentos})
          </h3>

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#E2B84A]"></div>
            </div>
          ) : data.lancamentos.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-[#716A80] mx-auto mb-4" />
              <p className="text-[#A19BAA]">Nenhum lançamento neste período</p>
              <p className="text-[#716A80] text-sm mt-1">Clique em "Lançar" para registrar</p>
            </div>
          ) : (
            <div className="space-y-2">
              {data.lancamentos.map(l => (
                <div 
                  key={l.id} 
                  data-testid={`lancamento-${l.id}`}
                  className="bg-[#050505] border border-[#2D243F] rounded-lg p-4 flex items-center gap-4 hover:border-[#E2B84A]/30 transition-colors"
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    l.tipo === 'entrada' ? 'bg-green-500/20' : 'bg-red-500/20'
                  }`}>
                    {l.tipo === 'entrada' ? 
                      <TrendingUp className="w-5 h-5 text-green-400" /> :
                      <TrendingDown className="w-5 h-5 text-red-400" />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {l.descricao}
                    </p>
                    <div className="flex flex-wrap gap-2 text-xs text-[#716A80] mt-1">
                      <span className="capitalize">{l.categoria.replace(/_/g, ' ')}</span>
                      <span>•</span>
                      <span>{new Date(l.data).toLocaleDateString('pt-BR')}</span>
                      <span>•</span>
                      <span className="capitalize">{l.forma_pagamento}</span>
                    </div>
                  </div>
                  <div className={`font-black text-lg flex-shrink-0 ${l.tipo === 'entrada' ? 'text-green-400' : 'text-red-400'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {l.tipo === 'entrada' ? '+' : '-'}R$ {l.valor.toFixed(2)}
                  </div>
                  <button
                    onClick={() => handleDelete(l.id)}
                    data-testid={`delete-lancamento-${l.id}`}
                    className="text-red-400 hover:text-red-300 p-2 flex-shrink-0"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#120E1A] border-2 border-[#FFD700] rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-black text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Novo Lançamento
              </h3>
              <button onClick={() => setShowAdd(false)} data-testid="close-add"><X className="text-[#A19BAA] hover:text-white" /></button>
            </div>

            <div className="space-y-4">
              {/* Tipo Selector */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setFormData({ ...formData, tipo: 'entrada', categoria: 'mensalidade' })}
                  data-testid="tipo-entrada-button"
                  className={`py-3 px-4 rounded-lg font-bold transition-colors ${
                    formData.tipo === 'entrada' 
                      ? 'bg-green-500 text-white' 
                      : 'bg-[#050505] border border-[#2D243F] text-[#A19BAA] hover:border-green-500/50'
                  }`}
                >
                  <TrendingUp className="inline-block mr-2" size={18} />
                  Entrada
                </button>
                <button
                  onClick={() => setFormData({ ...formData, tipo: 'saida', categoria: 'acao_social' })}
                  data-testid="tipo-saida-button"
                  className={`py-3 px-4 rounded-lg font-bold transition-colors ${
                    formData.tipo === 'saida' 
                      ? 'bg-red-500 text-white' 
                      : 'bg-[#050505] border border-[#2D243F] text-[#A19BAA] hover:border-red-500/50'
                  }`}
                >
                  <TrendingDown className="inline-block mr-2" size={18} />
                  Saída
                </button>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Categoria *</label>
                <select
                  value={formData.categoria}
                  onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                  data-testid="categoria-select"
                  className="w-full bg-[#050505] border border-[#2D243F] rounded-md px-4 py-3 text-white focus:border-[#FFD700] outline-none"
                >
                  {currentCategorias.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Descrição *</label>
                <input
                  type="text"
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  data-testid="descricao-input"
                  className="w-full bg-[#050505] border border-[#2D243F] rounded-md px-4 py-3 text-white focus:border-[#FFD700] outline-none"
                  placeholder="Ex: Mensalidade Maria Silva - Abril"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-white font-medium mb-2">Valor (R$) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.valor}
                    onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                    data-testid="valor-input"
                    className="w-full bg-[#050505] border border-[#2D243F] rounded-md px-4 py-3 text-white focus:border-[#FFD700] outline-none"
                    placeholder="0,00"
                  />
                </div>
                <div>
                  <label className="block text-white font-medium mb-2">Data *</label>
                  <input
                    type="date"
                    value={formData.data}
                    onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                    data-testid="data-input"
                    className="w-full bg-[#050505] border border-[#2D243F] rounded-md px-4 py-3 text-white focus:border-[#FFD700] outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Forma de Pagamento</label>
                <select
                  value={formData.forma_pagamento}
                  onChange={(e) => setFormData({ ...formData, forma_pagamento: e.target.value })}
                  data-testid="forma-pagamento-select"
                  className="w-full bg-[#050505] border border-[#2D243F] rounded-md px-4 py-3 text-white focus:border-[#FFD700] outline-none"
                >
                  <option value="pix">💸 PIX</option>
                  <option value="dinheiro">💵 Dinheiro</option>
                  <option value="cartao">💳 Cartão</option>
                  <option value="transferencia">🏦 Transferência</option>
                  <option value="boleto">📄 Boleto</option>
                </select>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Observações</label>
                <textarea
                  value={formData.observacoes}
                  onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                  data-testid="observacoes-input"
                  className="w-full bg-[#050505] border border-[#2D243F] rounded-md px-4 py-3 text-white focus:border-[#FFD700] outline-none"
                  rows="2"
                />
              </div>

              {/* Comprovante (obrigatório para saídas) */}
              <div className={`bg-[#050505] rounded-lg p-4 border ${formData.tipo === 'saida' ? 'border-red-500/40' : 'border-[#2D243F]'}`}>
                <p className="text-white font-bold mb-2 flex items-center gap-2">
                  📎 Comprovante / Nota Fiscal
                  {formData.tipo === 'saida' && <span className="text-red-400 text-xs">* obrigatório</span>}
                </p>
                {formData.comprovante_url ? (
                  <div className="flex items-center gap-3">
                    <img src={`${API_URL}${formData.comprovante_url}`} alt="comprovante" className="w-20 h-20 object-cover rounded-lg border border-[#FFD700]/40" />
                    <div className="flex-1">
                      <p className="text-green-400 text-sm font-bold">✓ Anexado</p>
                      <button onClick={() => setFormData({ ...formData, comprovante_url: '' })} data-testid="remove-comprovante" className="text-red-400 text-xs hover:underline">
                        Remover
                      </button>
                    </div>
                  </div>
                ) : (
                  <label className="cursor-pointer bg-[#E2B84A] hover:bg-[#F0D98C] text-white font-bold rounded-lg px-4 py-2 text-sm flex items-center gap-2 w-fit" data-testid="upload-comprovante-label">
                    {uploadingComp ? 'Enviando...' : <><Upload size={14} /> Anexar comprovante</>}
                    <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onUploadComprovante} disabled={uploadingComp} data-testid="comprovante-input" />
                  </label>
                )}
              </div>

              {/* Fotos do evento (até 2) */}
              <div className="bg-[#050505] rounded-lg p-4 border border-[#2D243F]">
                <p className="text-white font-bold mb-2 flex items-center gap-2">
                  <FileImage size={16} /> Fotos do Evento (opcional, até 2)
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                  {(formData.fotos_evento || []).map((url, i) => (
                    <div key={i} className="relative" data-testid={`foto-evento-${i}`}>
                      <img src={`${API_URL}${url}`} alt={`foto-${i}`} className="w-20 h-20 object-cover rounded-lg border border-[#FFD700]/40" />
                      <button
                        onClick={() => setFormData({ ...formData, fotos_evento: formData.fotos_evento.filter((_, idx) => idx !== i) })}
                        data-testid={`remove-foto-${i}`}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  {(formData.fotos_evento || []).length < 2 && (
                    <label className="cursor-pointer bg-[#2D243F] hover:bg-[#3D3352] text-white font-bold rounded-lg px-4 py-2 text-sm flex items-center gap-2" data-testid="upload-foto-label">
                      {uploadingFoto ? 'Enviando...' : <><Upload size={14} /> Adicionar foto</>}
                      <input type="file" accept="image/*" className="hidden" onChange={onUploadFotoEvento} disabled={uploadingFoto} data-testid="foto-evento-input" />
                    </label>
                  )}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowAdd(false)}
                  data-testid="cancel-add-button"
                  className="flex-1 bg-transparent border border-[#2D243F] text-[#A19BAA] font-bold rounded-lg px-4 py-3 hover:bg-[#2D243F] transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAdd}
                  disabled={saving}
                  data-testid="save-lancamento-button"
                  className="flex-1 bg-[#FFD700] hover:bg-[#F4C430] text-black font-bold rounded-lg px-4 py-3 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LivroCaixaPage;
